import { getBreadcrumbItems } from './breadcrumbs';
import type { SemanticFaqItem, SemanticHubPage, SemanticPage } from './types';

const SITE_URL = 'https://mapleos.ai';
const DEFAULT_IMAGE = `${SITE_URL}/images/mapleos-screenshot.png`;

function toAbsoluteUrl(path: string) {
  return `${SITE_URL}${path}`;
}

function getSchemaType(page: SemanticPage | SemanticHubPage) {
  const schemaHints = page.schemaHints || [];

  if (schemaHints.includes('softwareApplication')) return 'SoftwareApplication';
  if (schemaHints.includes('faq')) return 'FAQPage';
  if (schemaHints.includes('howTo')) return 'HowTo';
  if (schemaHints.includes('collectionPage')) return 'CollectionPage';

  return 'TechArticle';
}

export function getSemanticPageMetadata(page: SemanticPage | SemanticHubPage) {
  return {
    title: page.ogTitle || page.title,
    description: page.ogDescription || page.description,
    canonical: toAbsoluteUrl(page.path),
    openGraph: {
      title: page.ogTitle || page.title,
      description: page.ogDescription || page.description,
      url: toAbsoluteUrl(page.path),
      images: [{ url: DEFAULT_IMAGE }],
      type: 'article',
    },
    twitter: {
      card: 'summary_large_image',
      title: page.ogTitle || page.title,
      description: page.ogDescription || page.description,
      images: [DEFAULT_IMAGE],
    },
  };
}

export function buildSemanticStructuredData(page: SemanticPage | SemanticHubPage) {
  const breadcrumbs = getBreadcrumbItems(page);
  const faq = page.faq || [];

  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': getSchemaType(page),
    name: page.title,
    headline: page.title,
    description: page.description,
    url: toAbsoluteUrl(page.path),
    image: DEFAULT_IMAGE,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: toAbsoluteUrl(item.path),
    })),
  };

  const faqSchema = faq.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item: SemanticFaqItem) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }
    : null;

  return [baseSchema, breadcrumbSchema, faqSchema].filter(Boolean);
}
